import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  CreateReceiptPayload,
  CreateReceiptResponse,
  GenerateLinkPayload,
  GenerateLinkResponse,
  GenerateMemberPaymentLinkPayload,
  GenerateMemberPaymentLinkResponse,
  InscriptionPlayersPayment,
  InscriptionTeamsPayment,
  LicensePayment,
  Movement,
  NativeReturnRequest,
  PaymentInscriptionResponse,
  PaymentLicenseResponse,
  RedsysFormParams,
} from '@models';

export interface PurchaseBonoPayload {
  bonoId: string;
  successUrl: string;
  cancelUrl: string;
  /** Native app (#730): deep-link return paths; takes priority over successUrl/cancelUrl. */
  nativeReturn?: NativeReturnRequest;
}

export interface PurchaseBonoResponse {
  success: boolean;
  memberBonoId?: string;
  redirectUrl?: string;
  redsysForm?: RedsysFormParams;
  /** Redsys (#730): payments `/redsys-launch` URL the native app opens instead of POSTing redsysForm. */
  redsysLaunchUrl?: string;
}

/** Options for the gateway-starting methods of PaymentService (#730, R20-R22). */
export interface PaymentGatewayOptions {
  /** `false` in the native app: the gateway is opened with Browser. Defaults to `true`. */
  submitRedsysForm?: boolean;
}
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly functions = inject(Functions);
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);

  private submitRedsysForm(redsysForm: RedsysFormParams): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = redsysForm.url;

    const fields: Record<string, string> = {
      Ds_SignatureVersion: redsysForm.Ds_SignatureVersion,
      Ds_MerchantParameters: redsysForm.Ds_MerchantParameters,
      Ds_Signature: redsysForm.Ds_Signature,
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  async inscriptionTeamsPayment(
    payload: InscriptionTeamsPayment,
    options?: PaymentGatewayOptions,
  ) {
    const callable = httpsCallable<
      InscriptionTeamsPayment,
      PaymentInscriptionResponse
    >(this.functions, 'inscriptionTeamsPayment');
    const res = await callable(payload);
    if (res.data?.redsysForm && options?.submitRedsysForm !== false) {
      this.submitRedsysForm(res.data.redsysForm);
    }
    return res;
  }

  async inscriptionPlayersPayment(
    payload: InscriptionPlayersPayment,
    options?: PaymentGatewayOptions,
  ) {
    const callable = httpsCallable<
      InscriptionPlayersPayment,
      PaymentInscriptionResponse
    >(this.functions, 'inscriptionPlayersPayment');
    const res = await callable(payload);
    if (res.data?.redsysForm && options?.submitRedsysForm !== false) {
      this.submitRedsysForm(res.data.redsysForm);
    }
    return res;
  }

  generateInvoice(
    movementsIds: string[],
  ): Promise<import('@angular/fire/functions').HttpsCallableResult<boolean>> {
    const callable = httpsCallable<{ movementsIds: string[] }, boolean>(
      this.functions,
      'generateInvoice',
    );
    return callable({ movementsIds });
  }

  createPaymentLink(
    payload: GenerateLinkPayload,
  ): Promise<
    import('@angular/fire/functions').HttpsCallableResult<GenerateLinkResponse>
  > {
    const callable = httpsCallable<GenerateLinkPayload, GenerateLinkResponse>(
      this.functions,
      'generateProxyPaymentLink',
    );
    return callable(payload);
  }

  createReceipt(
    payload: CreateReceiptPayload,
  ): Promise<
    import('@angular/fire/functions').HttpsCallableResult<CreateReceiptResponse>
  > {
    const callable = httpsCallable<CreateReceiptPayload, CreateReceiptResponse>(
      this.functions,
      'createReceipt',
    );
    return callable(payload);
  }

  /**
   * Inicia el cobro de una licencia.
   *
   * `onResponse` (opcional) se invoca **síncronamente con `res.data` ANTES** de
   * `submitRedsysForm(...)`: la rama Redsys navega dentro de este mismo método
   * (`form.submit()`), así que el código del caller posterior al `await` corre
   * cuando la redirección ya se ha disparado. Cualquier persistencia que deba
   * ocurrir antes de salir a la pasarela tiene que hacerse en este hook.
   */
  async payLicense(
    data: LicensePayment,
    onResponse?: (response: PaymentLicenseResponse) => void,
    options?: PaymentGatewayOptions,
  ) {
    const callable = httpsCallable<LicensePayment, PaymentLicenseResponse>(
      this.functions,
      'payLicense',
    );
    const res = await callable(data);
    if (res.data) {
      onResponse?.(res.data);
    }
    if (res.data?.redsysForm && options?.submitRedsysForm !== false) {
      this.submitRedsysForm(res.data.redsysForm);
    }
    return res;
  }

  async generateMemberPaymentLink(
    payload: GenerateMemberPaymentLinkPayload,
  ): Promise<GenerateMemberPaymentLinkResponse> {
    const callable = httpsCallable<
      GenerateMemberPaymentLinkPayload,
      GenerateMemberPaymentLinkResponse
    >(this.functions, 'generateMemberPaymentLink');
    const res = await callable(payload);
    if (res.data?.redsysForm) {
      this.submitRedsysForm(res.data.redsysForm);
    }
    return res.data;
  }

  async purchaseBono(
    payload: PurchaseBonoPayload,
    options?: PaymentGatewayOptions,
  ): Promise<PurchaseBonoResponse> {
    const callable = httpsCallable<PurchaseBonoPayload, PurchaseBonoResponse>(
      this.functions,
      'purchaseBonoPaymentLink',
    );
    const res = await callable(payload);
    if (res.data?.redsysForm && options?.submitRedsysForm !== false) {
      this.submitRedsysForm(res.data.redsysForm);
    }
    return res.data;
  }

  get(id: string) {
    return this.firestore.getDocument<Movement>(
      this.tenantContext.collections.MOVEMENTS,
      id,
    );
  }
}
